import { useEffect, useRef, useState } from "react";
import { LiaSearchPlusSolid, LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import ConfirmDeleteModal from "../../common/DeleteModel";
import { dieMasterAPI } from "../../utlis/apiService";
import DieForm from "./DieForm";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import Button from "../../common/Button";
import { formatDate } from "../../utlis/formateDate";
import { STATUS_COLORS } from "../../utlis/constant";

const DieMaster = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const today = new Date().toISOString()?.split("T")[0];
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    id: 0,
    dieNumber: "",
    dieDate: today,
    subDieNo: "",
    invoiceNo: "",
    profileName: "",
    seriesName: "",
    customer_Id: "",
    widthHeight: "",
    currentWeight: "",
    orderWeight: "",
    status_Id: "",
    supplier_Id: "",
    location_Id: "",
    range: "",
    range_Per: "",
    die_Pdf: null,
    die_Image: null,
    created_By: userId,
    toolingSize_Id: "",
    die_Type_Id: "",
    no_Of_Cavity: "",
    hardness: "",
    area_Sqmm: "",
    perimeter: "",
    profileThickness: "",
  });
  const [loading, setLoading] = useState(false);
  const [isDieMasterData, setIsDieMasterData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchDieMasterData = async () => {
    setLoading(true);
    try {
      const res = await dieMasterAPI.getAllDieMaster(currentPage, pageSize);
      if (currentPage === 1) {
        setIsDieMasterData(res.data || []);
      } else {
        setIsDieMasterData((prev) => [...prev, ...(res.data || [])]);
      }

      if (!res.data || res.data.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      isFetchingRef.current = false;
    } catch (error) {
      notify("Error fetching die master data", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  useEffect(() => {
    fetchDieMasterData();
  }, [currentPage]);

  const handleAdd = () => {
    setIsModelOpen(true);
    setIsEditing(false);
    setFormData(blankDieForm);
  };

  const handleEdit = async (id) => {
    try {
      const data = await dieMasterAPI.getById(id);

      if (data.data) {
        setFormData({
          id: data.data.id,
          dieNumber: data.data.dieNumber,
          dieDate: data.data.dieDate,
          subDieNo: data.data.subDieNo,
          invoiceNo: data.data.invoice_No,
          profileName: data.data.profile_Name,
          seriesName: data.data.series_Name,
          customer_Id: data.data.customer_Id,
          widthHeight: data.data.width_Height,
          currentWeight: data.data.currentWeight,
          orderWeight: data.data.orderWeight,
          status_Id: data.data.status_Id,
          supplier_Id: data.data.supplier_Id,
          location_Id: data.data.location_Id,
          range: data.data.to_Kg ?? data.data.To_Kg ?? "",
          range_Per: data.data.range_Per,
          die_Pdf: data.data.die_Pdf,
          die_Image: data.data.die_Image,
          toolingSize_Id: data.data.tooling_Size,
          die_Type_Id: data.data.die_Type_Id,
          no_Of_Cavity: data.data.no_Of_Cavity,
          hardness: data.data.hardness,
          area_Sqmm: data.data.area_Sqmm,
          perimeter: data.data.perimeter,
          profileThickness: data.data.profile_Thickness,
          created_By: userId,
        });

        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (error) {
      notify("Failed to fetch die master details", "error");
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const blankDieForm = {
    id: 0,
    dieNumber: "",
    dieDate: today,
    subDieNo: "",
    invoiceNo: "",
    profileName: "",
    seriesName: "",
    customerName: "",
    widthHeight: "",
    currentWeight: "",
    orderWeight: "",
    status_Id: "",
    supplier_Id: "",
    location_Id: "",
    range: "",
    die_Pdf: null,
    die_Image: null,
    created_By: userId,
    toolingSize_Id: "",
    die_Type_Id: "",
    no_Of_Cavity: "",
    hardness: "",
    area_Sqmm: "",
    perimeter: "",
    profileThickness: "",
  };

  const handleConfirmDelete = async () => {
    try {
      await dieMasterAPI.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchDieMasterData();
    } catch (error) {
      notify("Failed to delete record", "error");
    }
    setIsDeleteModalOpen(false);
    setSelectedId(null);
  };

  const handleSubmit = async (submittedData, type) => {
    setLoading(true);
    try {
      await dieMasterAPI.saveOrUpdate(submittedData);
      notify("Saved successfully", "success");

      if (type === "blank") {
        setFormData({
          ...blankDieForm,
          created_By: userId,
        });
      } else if (type === "sameInvoice") {
        setFormData({
          ...blankDieForm,
          invoiceNo: submittedData.get("Invoice_No"),
          supplier_Id: submittedData.get("Supplier_Id"),
          created_By: userId,
        });
      } else {
        setIsModelOpen(false);
        setFormData(blankDieForm);
      }

      fetchDieMasterData();
    } catch (err) {
      notify("Save failed", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div>
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col">
            <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">
              Die Master
            </h2>
            <p className="text-sm text-gray-500">
              Manage and track your die inventory
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-72 group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LiaSearchSolid className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Search by Die No..."
                className="block w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-red-500 transition-colors"
                >
                  <MdClose size={18} />
                </button>
              )}
            </div>

            <Button
              text="Add Die"
              className="bg-orange text-black hover:bg-white font-medium px-4 py-2 rounded-lg flex items-center shadow-sm transition-all"
              icon={HiOutlinePlus}
              onClick={handleAdd}
            />
          </div>
        </div>

        <div
          ref={tableScrollRef}
          className="relative overflow-auto shadow-inner"
          style={{ height: "calc(100vh - 260px)" }}
        >
          {loading && currentPage === 1 ? (
            <Loader />
          ) : (
            <div className="min-w-max">
              <Table
                columns={[
                  { key: "dieNumber", label: "Die No." },
                  { key: "subDieNo", label: "SubDie No." },
                  { key: "invoice_No", label: "Invoice No." },
                  { key: "profile_Name", label: "Profile" },
                  { key: "series_Name", label: "Series" },
                  { key: "customer_Name", label: "Customer" },
                  { key: "width_Height", label: "Width * Height (mm)" },
                  {
                    key: "status_Name",
                    label: "Status",
                    render: (val) => {
                      const color =
                        STATUS_COLORS[val] || "bg-slate-100 text-slate-600";
                      return (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}
                        >
                          {val}
                        </span>
                      );
                    },
                  },
                  { key: "die_Range", label: "Range" },
                  { key: "currentWeight", label: "Current Wt/12ft (kg)" },
                  { key: "orderWeight", label: "Order Wt/12ft (kg)" },
                  { key: "supplier_Name", label: "Supplier" },
                  { key: "location_Name", label: "Location" },
                  {
                    key: "die_Pdf",
                    label: "PDF",
                    render: (value) =>
                      value ? (
                        <a
                          href={value}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View File
                        </a>
                      ) : (
                        <span className="text-gray-400">No File</span>
                      ),
                  },
                  {
                    key: "created_Date",
                    label: "Created Date",
                    render: (value) => formatDate(value),
                  },
                  {
                    key: "modify_Date",
                    label: "Modified Date",
                    render: (value) => formatDate(value),
                  },
                ]}
                data={isDieMasterData}
                onEdit={(row) => handleEdit(row.id)}
                onDelete={(row) => handleDelete(row.id)}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}

      {isModelOpen && (
        <DieForm
          initialData={formData}
          key={JSON.stringify(formData)}
          onCancel={() => {
            setIsModelOpen(false);
            setFormData(blankDieForm);
          }}
          onSubmit={handleSubmit}
          isEditing={isEditing}
          isSaving={loading}
        />
      )}
    </>
  );
};

export default DieMaster;
